import { Box } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import i18n from '../../i18n';
import type { ResolvedZone, ZoneSize } from './geoFlags';

/**
 * Renders a country flag (SVG via flagcdn.com — crisp on every OS, no bundled
 * assets) for a resolved zone/language, or a neutral icon when there's no
 * country (worldwide / continent / unresolved). See geoFlags.ts.
 */
/**
 * Regional flags flagcdn does not serve (404), for the regional languages of `LANG_FLAG`:
 * Catalonia (senyera), the Basque Country (ikurriña), Galicia (civil flag, no arms).
 */
const LOCAL_FLAGS: Record<string, string> = {
  'es-ct': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 6"><rect width="9" height="6" fill="#FCDD09"/>${[1, 3, 5, 7]
    .map((i) => `<rect y="${(i * 6) / 9}" width="9" height="${6 / 9}" fill="#DA121A"/>`)
    .join('')}</svg>`,
  'es-pv': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 14"><rect width="25" height="14" fill="#D52B1E"/>'
    + '<path d="M0 0L25 14M25 0L0 14" stroke="#009B48" stroke-width="2.2"/>'
    + '<path d="M12.5 0V14M0 7H25" stroke="#FFFFFF" stroke-width="2.2"/></svg>',
  'es-ga': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 20"><rect width="30" height="20" fill="#FFFFFF"/>'
    + '<path d="M0 0L30 20" stroke="#0099CC" stroke-width="5"/></svg>',
};

export function Flag({ countryCode, size = 22, title }: { countryCode?: string; size?: number; title?: string }) {
  if (!countryCode) {
    return <NeutralIcon size={size} kind="world" />;
  }
  const local = LOCAL_FLAGS[countryCode];
  return (
    <Box
      component="img"
      src={local ? `data:image/svg+xml;utf8,${encodeURIComponent(local)}` : `https://flagcdn.com/${countryCode}.svg`}
      alt={title || countryCode}
      title={title}
      loading="lazy"
      sx={{
        width: size,
        height: Math.round(size * 0.72),
        objectFit: 'cover',
        borderRadius: 0.75,
        boxShadow: 'inset 0 0 0 1px rgba(11,11,15,0.12)',
        flexShrink: 0,
        display: 'block',
      }}
    />
  );
}

function NeutralIcon({ size, kind }: { size: number; kind: 'world' | 'continent' | 'pin' }) {
  const Icon = kind === 'world' ? PublicIcon : kind === 'continent' ? LanguageIcon : LocationOnIcon;
  return (
    <Box sx={{ width: size, height: Math.round(size * 0.72), display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <Icon sx={{ fontSize: size * 0.85, color: 'text.secondary' }} />
    </Box>
  );
}

/** Flag for a resolved ZONE — flag when there's a country, else a size-aware icon. */
export function ZoneFlag({ zone, size = 22, title }: { zone: ResolvedZone; size?: number; title?: string }) {
  if (zone.countryCode) return <Flag countryCode={zone.countryCode} size={size} title={title} />;
  const kind: 'world' | 'continent' | 'pin' = zone.size === 'world' ? 'world' : zone.size === 'continent' ? 'continent' : 'pin';
  return <NeutralIcon size={size} kind={kind} />;
}

const SIZE_LABEL_KEY: Record<ZoneSize, string> = {
  world: 'flag.size.world',
  continent: 'flag.size.continent',
  country: 'flag.size.country',
  region: 'flag.size.region',
  city: 'flag.size.city',
  village: 'flag.size.village',
};

export function sizeLabel(size: ZoneSize): string {
  return i18n.t(SIZE_LABEL_KEY[size]);
}
